import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, X, FileSearch } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function SecondOpinion() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", medical_history: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const addFiles = (list) => {
    const arr = Array.from(list);
    setFiles((prev) => [...prev, ...arr].slice(0, 8));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const file_paths = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_paths.push(up.data.storage_path);
      }
      await api.post("/second-opinions", { ...form, file_paths });
      toast.success("Request submitted! A specialist will call you within 24 hours.");
      setForm({ name: "", mobile: "", email: "", medical_history: "" });
      setFiles([]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="second-opinion-page">
      <section className="hero-bg py-12">
        <div className="container-x">
          <span className="font-cursive text-2xl text-brand-green">Get Second Opinion</span>
          <h1 className="text-4xl sm:text-5xl font-bold">Confidence in your care decision.</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Upload your reports, prescriptions or scans — our specialist will review and respond within 24 hours.</p>
        </div>
      </section>

      <section className="container-x py-10">
        <form onSubmit={submit} className="card-clean p-6 md:p-10 max-w-3xl mx-auto grid gap-4" data-testid="second-opinion-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="so-name" />
            <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile (for callback)" data-testid="so-mobile" />
            <input type="email" className="sm:col-span-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" data-testid="so-email" />
          </div>
          <textarea rows="5" value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} placeholder="Briefly describe your condition, current treatment, and what advice you need…" data-testid="so-history" required />

          <label className="border border-dashed border-slate-300 rounded-xl p-6 bg-slate-50 cursor-pointer text-center block">
            <Upload className="h-7 w-7 mx-auto text-brand-purple" />
            <div className="mt-2 text-sm font-semibold">Upload reports, prescriptions or scans</div>
            <div className="text-xs text-slate-500">PDF / JPG / PNG (max 8 files, 10MB each)</div>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="so-files" />
          </label>

          {files.length > 0 && (
            <ul className="space-y-2" data-testid="so-file-list">
              {files.map((f, i) => (
                <li key={i} className="flex justify-between items-center bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 truncate"><FileSearch className="h-4 w-4 text-brand-purple shrink-0" /> {f.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500" data-testid={`so-remove-file-${i}`}><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}

          <button disabled={submitting} className="btn-primary justify-center mt-2" data-testid="so-submit">{submitting ? "Submitting…" : "Request Second Opinion"}</button>
        </form>
      </section>
    </div>
  );
}
